import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import { Field, useForm } from 'react-final-form';
import { FormError } from '@/core/FormError.atom';
import clsx from 'clsx';
import AsyncTypeahead from 'react-bootstrap-typeahead/types/components/AsyncTypeahead/AsyncTypeahead';
import Highlighter from 'react-bootstrap-typeahead/types/components/Highlighter';
import type { Location } from '@/impactReports/impactReport.types';
import { Loader } from '@googlemaps/js-api-loader';
import { logError } from '@/utilities/logger';
import { errorToast } from '@/utilities/toast.utilities';

const loader = new Loader({
  apiKey: 'AIzaSyDUIZefRvTSXPC2YdnvXTqgt6GWqlXPWqw',
  libraries: ['places'],
});

export interface LocationLookupFormComponentProps extends ValidatingFormComponent<Location> {
  component: 'LocationLookupFormComponent';
  placeholder?: string;
}

/**
 * This form component is integrated with the GoogleMaps API and exposes the longitude and latitude of the provided
 * address. This is used to place locations on Maps (for impact reports, for example).
 */
export const LocationLookupFormComponent: React.FunctionComponent<LocationLookupFormComponentProps> = (props) => {
  const {
    placeholder,
    extraClassNames,
    testId = 'selectLocation',
    name,
    customErrorText,
    onChange,
    value,
    required,
  } = props;

  const formState = useForm().getState();
  const showError = _.has(formState.errors, name) && (_.has(formState.dirtyFields, name) || formState.submitFailed);
  const [location, setSelectedLocation] = useState<Location | undefined>(value);
  const defaultValidation = (value: Location) => !!(required && _.get(value, 'placeId', undefined));

  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<Location[]>([]);
  const autocompleteRef = useRef<google.maps.places.AutocompleteSuggestion | null>(null);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    setSelectedLocation(value);
  }, [value]);

  const handleSearch = async (query: string) => {
    if (!query) setSelectedLocation(undefined);
    if (!query) return;
    setIsLoading(true);

    if (!autocompleteRef.current) {
      try {
        await loader.load();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { AutocompleteSuggestion, AutocompleteSessionToken } = await google.maps.importLibrary('places');
        autocompleteRef.current = AutocompleteSuggestion;
        tokenRef.current = new AutocompleteSessionToken();
      } catch (error: any) {
        logError('Failed to load Google Maps API:', error);
        errorToast({ messageKey: 'GOOGLE_MAPS_LOAD_ERROR' });
        setIsLoading(false);
        setOptions([]);
        return;
      }
    }

    const request = {
      input: query,
      includedPrimaryTypes: ['locality'],
      sessionToken: tokenRef.current,
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { suggestions } = await autocompleteRef.current.fetchAutocompleteSuggestions(request);
    if (_.isEmpty(suggestions)) {
      setIsLoading(false);
      setOptions([]);
      return;
    }
    const options = await Promise.all(
      suggestions.map(async (suggestion: any) => {
        const place = await suggestion.placePrediction.toPlace();
        await place.fetchFields({
          fields: ['formattedAddress', 'id', 'location'],
        });

        return {
          placeId: place.id,
          label: place.formattedAddress,
          latitude: place.location.lat(),
          longitude: place.location.lng(),
        };
      }),
    );

    setOptions(options);
    setIsLoading(false);
  };

  const handleSelect = async (selected: Location[]) => {
    const opt = selected[0];
    if (!opt) {
      setSelectedLocation(undefined);
      // we need to call onChange with undefined here to ensure we can clear the entry
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange && onChange(undefined);
      return;
    }

    onChange && onChange(opt);
  };

  return (
    <div className={clsx('flexRowContainer', extraClassNames)} data-testid={testId}>
      <Field name={name} validate={defaultValidation}>
        {({ input, meta }) => {
          return (
            <>
              <AsyncTypeahead
                labelKey={(option) => {
                  if (option) {
                    return _.get(option, 'label');
                  } else {
                    return '';
                  }
                }}
                isLoading={isLoading}
                onSearch={async (query: string) => {
                  await handleSearch(query);
                }}
                placeholder={placeholder}
                id="locationLookup"
                onChange={async (selected) => {
                  await handleSelect(selected as any);
                }}
                clearButton={true}
                selected={location ? [location] : undefined}
                filterBy={() => true}
                multiple={false}
                options={options}
                renderMenuItemChildren={renderMenu}
              />
            </>
          );
        }}
      </Field>
      {showError && customErrorText && <FormError errorText={customErrorText} dataTestId={`${testId}Error`} />}
    </div>
  );
};

const renderMenu = (option: any, props: any) => {
  return (
    <div>
      <Highlighter search={props.text}>{`${option.label}`}</Highlighter>
    </div>
  );
};
